import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Divider } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { userService } from '../services/api';

const { Title, Text } = Typography;

const ProfilePage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await userService.getProfile();
                if (response.status === 'success') {
                    form.setFieldsValue({
                        name: response.data.name,
                        email: response.data.email,
                        username: response.data.username
                    });
                }
            } catch (error) {
                console.error("Error fetching profile", error);
                message.error("No se pudo cargar la información del perfil");
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [form]);

    const onFinish = async (values) => {
        setSubmitting(true);
        try {
            const response = await userService.updateProfile(values);
            if (response.status === 'success') {
                message.success("Perfil actualizado correctamente");
                // Clear password fields
                form.setFieldsValue({
                    password: '',
                    confirm: ''
                });
            }
        } catch (error) {
            const errorMsg = error.response?.data?.data?.message || "Error al actualizar el perfil";
            message.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fade-in">
            <Title level={2}><EditOutlined /> Mi Perfil</Title>
            <Text type="secondary">Gestiona tu información personal y seguridad de cuenta.</Text>

            <Divider />

            <div style={{ maxWidth: 600, margin: '0 auto' }}>
                <Card loading={loading} bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        requiredMark={false}
                    >
                        <Title level={4} style={{ marginBottom: 20 }}>Información Básica</Title>

                        <Form.Item
                            label="Nombre Completo"
                            name="name"
                            rules={[{ required: true, message: 'Por favor ingresa tu nombre' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Nombre Completo" size="large" />
                        </Form.Item>

                        <Form.Item
                            label="Correo Electrónico"
                            name="email"
                            rules={[
                                { required: true, message: 'Por favor ingresa tu email' },
                                { type: 'email', message: 'Ingresa un correo válido' }
                            ]}
                        >
                            <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
                        </Form.Item>

                        <Form.Item
                            label="Nombre de Usuario"
                            name="username"
                            rules={[{ required: true, message: 'Por favor ingresa tu usuario' }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder="Usuario" size="large" />
                        </Form.Item>

                        <Divider style={{ margin: '40px 0' }} />

                        <Title level={4} style={{ marginBottom: 20 }}>Seguridad (Cambio de Contraseña)</Title>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                            Deja estos campos en blanco si no deseas cambiar tu contraseña actual.
                        </Text>

                        <Form.Item
                            label="Nueva Contraseña"
                            name="password"
                            rules={[
                                { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Nueva Contraseña" size="large" />
                        </Form.Item>

                        <Form.Item
                            label="Confirmar Nueva Contraseña"
                            name="confirm"
                            dependencies={['password']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error('Las contraseñas no coinciden'));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder="Confirmar Contraseña" size="large" />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 40 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={submitting}
                                size="large"
                                block
                            >
                                Guardar Cambios
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </div>
    );
};

export default ProfilePage;
